import keyBy from "lodash-es/keyBy";
import {
  action,
  autorun,
  computed,
  observable,
  when,
  makeObservable,
} from "mobx";
import { fromPromise, type IPromiseBasedObservable, PENDING } from "mobx-utils";

import Api from "nvent-web/services/Api";
import { type ProductCatalog } from "nvent-web/services/resources/ProductCatalogResource";
import { CategoryType } from "nvent-web/types/CategoryType";
import { type NewProductSpec } from "nvent-web/types/NewProductSpec";
import { type Product } from "nvent-web/types/Product";
import { type ProductBase } from "nvent-web/types/ProductBase";
import { type ProductCategory } from "nvent-web/types/ProductCategory";
import { type ProductSku } from "nvent-web/types/ProductSku";
import { createInitialFromPromise } from "nvent-web/utils/createInitialFromPromise";
import { enumIndex, groupByEnum } from "nvent-web/utils/enum";

import { RoomsStore } from "./Rooms";
import { UserStore } from "./User";

const emptyCatalog: ProductCatalog = {
  products: [],
  productSkus: [],
  productCategories: [],
};

export class ProductsStore {
  newProductPromise?: IPromiseBasedObservable<void>;
  newProductTestPromise?: IPromiseBasedObservable<Product>;
  productsCatalogPromise =
    createInitialFromPromise<ProductCatalog>(emptyCatalog);
  productPromise?: IPromiseBasedObservable<Product>;
  removeProductPromise?: IPromiseBasedObservable<void>;
  removeThermostatPromise?: IPromiseBasedObservable<void>;
  removeAccessoryPromise?: IPromiseBasedObservable<void>;
  productBatchNumberPromise?: IPromiseBasedObservable<Product>;
  singleProduct?: Product;

  constructor(
    private user: UserStore,
    private rooms: RoomsStore,
    private api: Api
  ) {
    makeObservable(this, {
      newProductPromise: observable,
      newProductTestPromise: observable,
      productsCatalogPromise: observable,
      productPromise: observable,
      removeProductPromise: observable,
      removeThermostatPromise: observable,
      removeAccessoryPromise: observable,
      productBatchNumberPromise: observable,
      singleProduct: observable,
      isProductCatalogLoading: computed,
      productCatalog: computed,
      productCategories: computed,
      productSkus: computed,
      availableProductSkus: computed,
      availableProducts: computed,
      availableProductCategories: computed,
      productCategoriesByType: computed,
      availableProductCategoriesByType: computed,
      products: computed,
      availableThermostatSkus: computed,
      productsByType: computed,
      availableProductsByType: computed,
      product: computed,
      loadProductsCatalog: action.bound,
      loadProductsCatalogForCountry: action.bound,
      isProductCreating: computed,
      createProductSkus: action.bound,
      createProductSku: action.bound,
      isProductLoading: computed,
      getProduct: action.bound,
      testProduct: action.bound,
      setProductBatchNumber: action.bound,
      removeProduct: action.bound,
      removeThermostat: action.bound,
      removeAccessory: action.bound,
      removeAllProducts: action.bound,
      setSingleProduct: action.bound,
    });

    when(
      () => user.isReady,
      () => {
        this.loadProductsCatalog();
      }
    );

    autorun(() => {
      if (this.productPromise) {
        this.setSingleProduct(
          this.productPromise.case({ fulfilled: (r) => r })
        );
      }
    });
  }

  get isProductCatalogLoading(): boolean {
    return this.productsCatalogPromise.state === PENDING;
  }

  get productCatalog(): ProductCatalog {
    return (
      this.productsCatalogPromise.case({ fulfilled: (catalog) => catalog }) ||
      emptyCatalog
    );
  }

  get productCategories(): ProductCategory[] {
    return this.productCatalog.productCategories;
  }

  get productSkus(): ProductSku[] {
    return this.productCatalog.productSkus;
  }

  get availableProductSkus(): ProductSku[] {
    return this.productSkus.filter(({ availableCountries }) =>
      availableCountries.includes(this.user.companyAddress.countryCode)
    );
  }

  get availableProducts(): ProductBase[] {
    const productIds = new Set(
      this.availableProductSkus.map(({ productId }) => productId)
    );
    return this.productCatalog.products.filter(({ id }) => productIds.has(id));
  }

  get availableProductCategories(): ProductCategory[] {
    const categoryIds = new Set(
      this.availableProducts.map(({ productCategoryId }) => productCategoryId)
    );
    return this.productCategories.filter(({ id }) => categoryIds.has(id));
  }

  get productCategoriesByType() {
    return groupByEnum(
      CategoryType,
      this.productCategories,
      ({ type }) => type
    );
  }

  get availableProductCategoriesByType() {
    return groupByEnum(
      CategoryType,
      this.availableProductCategories,
      ({ type }) => type
    );
  }

  get products(): ProductBase[] {
    return this.productCatalog.products;
  }

  get availableThermostatSkus(): ProductSku[] {
    const thermostatCategory = this.availableProductCategories.find(
      ({ type }) => type === CategoryType.thermostat
    );
    const thermostatCategoryId = thermostatCategory
      ? thermostatCategory.id
      : null;

    if (!thermostatCategoryId) {
      return [];
    }

    const thermostats = this.availableProducts.filter(
      ({ productCategoryId }) => productCategoryId === thermostatCategoryId
    );

    const thermostatsById = keyBy(thermostats, ({ id }) => id);

    return this.availableProductSkus.filter(
      ({ productId }) => productId in thermostatsById
    );
  }

  get productsByType() {
    const categoriesById = keyBy(this.productCategories, (c) => c.id);

    const byType = enumIndex(CategoryType, () => [] as ProductBase[]);

    this.products.forEach((product) => {
      const category = categoriesById[product.productCategoryId];
      byType[category.type].push(product);
    });

    return byType;
  }

  get availableProductsByType() {
    const categoriesById = keyBy(this.availableProductCategories, (c) => c.id);

    const byType = enumIndex(CategoryType, () => [] as ProductBase[]);

    this.availableProducts.forEach((product) => {
      const category = categoriesById[product.productCategoryId];
      byType[category.type].push(product);
    });

    return byType;
  }

  get product(): Product | undefined {
    return (
      this.productPromise &&
      this.productPromise.case({ fulfilled: (product) => product })
    );
  }

  setSingleProduct(value: typeof this.singleProduct) {
    this.singleProduct = value;
  }

  loadProductsCatalog() {
    this.productsCatalogPromise = fromPromise(
      this.api.productCatalog.getCatalog().then(({ data }) => data)
    );
  }

  loadProductsCatalogForCountry(countryCode: string) {
    this.productsCatalogPromise = fromPromise(
      this.api.productCatalog
        .getCatalogForCountry(countryCode)
        .then(({ data }) => data)
    );
  }

  get isProductCreating(): boolean {
    return Boolean(
      this.newProductPromise && this.newProductPromise.state === PENDING
    );
  }

  async createProductSkus(
    projectId: number,
    roomId: number,
    specs: NewProductSpec[]
  ) {
    this.newProductPromise = fromPromise(
      this.api.products
        .createSkus(projectId, roomId, specs)
        .then(({ data }) => data)
    );

    await this.newProductPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.newProductPromise;
  }

  async createProductSku(
    projectId: number,
    roomId: number,
    spec: NewProductSpec
  ) {
    return this.createProductSkus(projectId, roomId, [spec]);
  }

  get isProductLoading(): boolean {
    return Boolean(
      this.productPromise && this.productPromise.state === PENDING
    );
  }

  getProduct(projectId: number, roomId: number, productId: number) {
    this.productPromise = fromPromise(
      this.api.products
        .getOne(projectId, roomId, productId)
        .then(({ data }) => data)
    );

    return this.productPromise;
  }

  async testProduct(
    projectId: number,
    roomId: number,
    productId: number,
    heatResistance: number,
    insulationResistance: number,
    testType: string
  ) {
    this.newProductTestPromise = fromPromise(
      this.api.products
        .test(
          projectId,
          roomId,
          productId,
          heatResistance,
          insulationResistance,
          testType
        )
        .then(({ data }) => data)
    );

    return this.newProductTestPromise;
  }

  async setProductBatchNumber({
    projectId,
    roomId,
    productId,
    batchNumber,
    reflectaSerialNumber,
  }: {
    projectId: number;
    roomId: number;
    productId: number;
    batchNumber: string | null;
    reflectaSerialNumber: string | null;
  }) {
    this.productBatchNumberPromise = fromPromise(
      this.api.products
        .setBatchNumber({
          projectId,
          roomId,
          productId,
          batchNumber,
          reflectaSerialNumber,
        })
        .then(({ data }) => data)
    );

    return this.productBatchNumberPromise;
  }

  async removeProduct(projectId: number, roomId: number, productId: number) {
    this.removeProductPromise = fromPromise(
      this.api.products
        .removeProduct(projectId, roomId, productId)
        .then(({ data }) => data)
    );
    await this.removeProductPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.removeProductPromise;
  }

  async removeThermostat(
    projectId: number,
    roomId: number,
    thermostatId: number
  ) {
    this.removeThermostatPromise = fromPromise(
      this.api.thermostat
        .remove(projectId, roomId, thermostatId)
        .then(({ data }) => data)
    );
    await this.removeThermostatPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.removeThermostatPromise;
  }

  async removeAccessory(
    projectId: number,
    roomId: number,
    accessoryId: number
  ) {
    this.removeAccessoryPromise = fromPromise(
      this.api.accessories
        .remove(projectId, roomId, accessoryId)
        .then(({ data }) => data)
    );
    await this.removeAccessoryPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.removeAccessoryPromise;
  }
  async removeAllProducts(projectId: number, roomId: number) {
    await this.api.products.deleteAllProducts(projectId, roomId);
    await this.rooms.getRoom(projectId, roomId);
  }
}
